import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getNavbarHeight, getViewportDimensions } from '../../../../models/Utils';

import { ADALandmarks, ADAARIARoles } from '../../../../models/ADA';

import './Slot.scss';

const mobileScreenThreshold = 767; // MUST BE IN SYNC WITH CSS
const spinSpeed = 4000; // pixels per second

const Slot = React.forwardRef(({ offsetX, totalWidth, unitWidth, unitHeight, themes, isSpinning, targetIndex }, theRef) => {
  const menuOpened = useSelector(state => state.data.menuOpened);
  const mediaPath = useSelector(state => state.config.basePath + state.config.apiMediaPath);
  const refRail = useRef(null);
  const refSpinTime = useRef(null);
  const refSpinningActive = useRef(false);
  const refShuffledThemeIndices = useRef([]);

  useEffect(() => {
    if (isSpinning) {
      refSpinningActive.current = true;
      spin();
    } else {
      refSpinningActive.current = false;
    }
  }, [isSpinning]);

  const spin = () => {
    refSpinTime.current = Date.now();
    const railTargetIndex = targetIndex !== -1 ? refShuffledThemeIndices.current.indexOf(targetIndex) : Math.floor(Math.random() * themes.length);
    const targetBottom =  - railTargetIndex * unitHeight;
    const animate = () => {
      if (!refRail.current) return;
      const t = Date.now();
      const currentBottom = parseFloat(refRail.current.style.bottom);
      const height = parseFloat(refRail.current.style.height);
      const newBottomTemp = currentBottom - spinSpeed * (t - refSpinTime.current) / 1000;
      const newBottom = newBottomTemp < -(height - unitHeight) ? newBottomTemp + (height - unitHeight) : newBottomTemp;
      refRail.current.style.bottom = `${newBottom}px`;
      refSpinTime.current = t;
      if (refSpinningActive.current) {
        requestAnimationFrame(animate);
      } else {
        if (newBottom > targetBottom) {
          requestAnimationFrame(animate);
        } else {
          refRail.current.style.bottom = `${targetBottom}px`;
        }
      }
    };
    requestAnimationFrame(animate);
  };

  const shuffleIndices = (count) => {
    const arr = Array.from(Array(count).keys());

    let currentIndex = arr.length,  randomIndex;

    // While there remain elements to shuffle...
    while (currentIndex != 0) {

      // Pick a remaining element...
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [arr[currentIndex], arr[randomIndex]] = [
        arr[randomIndex], arr[currentIndex]];
    }

    return arr;
  };

  const getThemesOnRail = (themes, offsetX, smProps) => {
    if (themes.length < 2) {
      throw new Error('There has to be at least two themes');
    }

    let fields = [];
    if (!refShuffledThemeIndices.current.length) {
      refShuffledThemeIndices.current = shuffleIndices(themes.length);
    }
    [...refShuffledThemeIndices.current, refShuffledThemeIndices.current[0]].forEach((themeInd, i) => {
      fields.push(
        <div className="ccw-sm-rail-field" key={i} style={{ bottom: `${i * smProps.height}px`, height: `${smProps.height}px` }}>
          <div
            className="ccw-sm-rf-inner"
            style={{ 
              width: `${smProps.width}px`,
              left: `-${offsetX}px`,
              backgroundImage: `url(${mediaPath}${getProperImage(themes[themeInd]).Image})`
            }}></div>
        </div>
      );
    });
    return fields;
  };

  const getGap = () => {
    if (getViewportDimensions().width <= mobileScreenThreshold) {
      return 2;
    } else {
      return 4;
    }
  };

  const getProperImage = (theme) => {
    if (getViewportDimensions().width <= mobileScreenThreshold) {
      return theme.MobileImage;
    } else {
      return theme.DesktopImage;
    }
  };

  return (
    <div className={`ccw-sm-slot ${isSpinning}`} ref={theRef} style={{ left: `${offsetX}px`, width: `${unitWidth}px` }}>
      <div className="ccw-sm-rail" ref={refRail} style={{ bottom: '0px', height: `${unitHeight * (themes.length + 1)}px` }}>
        {getThemesOnRail(themes, offsetX, { width: totalWidth, height: unitHeight })}
      </div>
    </div>
  );
});

export default Slot;